import type { CSSProperties } from "react"
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { plural, t } from "@lingui/core/macro"
import { isAndroid, isChrome, isEdge, isIOS } from "react-device-detect"
import { useParams } from "react-router-dom"
import AutoSizer from "react-virtualized-auto-sizer"
import { useAppContext } from "../../../hooks/appContext"
import { useDebounce } from "../../../hooks/useDebounce"
import { chatApi } from "../../../store/chatSlice"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import {
    addMessageToHistory,
    messagingApi,
    useGetProfileQuery,
    useGetThreadsQuery,
    useLazyGetHistoryQuery,
    useMarkAsReadMutation,
} from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import { Spinner } from "../../common"
import { ScrollButton } from "../../common/molecules/ScrollButton"
import { ToastManager } from "../common/Toast/ToastManager"
import { NEWRELIC_SOURCE } from "../constants"
import { draftCache } from "../DraftCache/DraftCache"
import { EmoticonDetails } from "../EmoticonDetails"
import { useMessagingView } from "../hooks"
import { useHover } from "../hooks/useHover"
import { useNewConversationTracking } from "../hooks/useNewConversationTracking"
import { useNewMessageLine } from "../hooks/useNewMessageLine"
import { useVisualViewport } from "../hooks/useVisualViewport"
import { MessageHeader } from "../MessageHeader/MessageHeader"
import { MessageInputBar } from "../MessageInputBar/MessageInputBar"
import { MessageMedia } from "../MessageMedia/MessageMedia"
import { MessageRow } from "../MessageRow/MessageRow"
import { findNonLogMessage } from "../MessageRow/utils"
import { MessageRowDisclaimer } from "../MessageRowDisclaimer/MessageRowDisclaimer"
import { createLogMessage, useMessagingContext } from "../MessagingContext"
import { Reactions } from "../Reactions"
import { ReportMessage } from "../ReportMessage/ReportMessage"
import { UserNote } from "../UserNote/UserNote"
import { classNames } from "../utils"
import { VirtualList } from "../VirtualList/VirtualList"
import type { MessageRowData } from "../MessageRow/MessageRow"
import type { IEmoticon } from "../RenderedMessage/utils"
import type { Message } from "../types"
import type { ListChildComponentProps, VariableSizeList } from "react-window"
import "./MessageList.scss"

const TEST_MEDIA_MESSAGE = false
const ABORT_ERROR_NAME = "AbortError"

const MESSAGES_BATCH_SIZE = 50
const OVERSCAN_COUNT = 5
const SCROLL_THRESHOLD = 84
const SCROLL_THROTTLE_DELAY = 500
const LOADER_HEIGHT = 32
const NEAR_BOTTOM_THRESHOLD = 70
const TYPING_INDICATOR_HEIGHT = 42
const TYPING_INDICATOR_HEIGHT_COMPACT = 30
const SCROLL_BUTTON_DEBOUNCE_DELAY = 100
const VIEWPORT_HEIGHT_CHANGE_THROTTLE_DELAY = 100
const USER_NOTE_DESKTOP_SCROLL_OFFSET = 148

const Row = (props: ListChildComponentProps<MessageRowData>) => (
    <MessageRow {...props} />
)

/** Prevents scroll jank by pre-measuring dynamic message heights for react-window */
const HeightPreCalculator = ({
    messages,
    messageHeights,
    itemData,
}: {
    messages: Message[]
    messageHeights: React.MutableRefObject<Record<string, number>>
    itemData: MessageRowData
}) => {
    const unmeasured = messages.filter((msg) => !messageHeights.current[msg.i])

    if (unmeasured.length === 0) return null

    return (
        <div
            style={{
                position: "absolute",
                opacity: 0,
                visibility: "hidden",
                pointerEvents: "none",
            }}
        >
            {unmeasured.map((message) => (
                <MessageRow
                    key={`shadow-${message.i}`}
                    index={messages.indexOf(message) + 1}
                    style={{ position: "relative" }}
                    data={{
                        ...itemData,
                    }}
                />
            ))}
        </div>
    )
}

type VisibleItems = {
    startIndex: number
    stopIndex: number
}

// we know this file is out of control and expect to refactor soon (MSG-175)
// eslint-disable-next-line complexity
export function MessageList() {
    const DM_RESTRICTION_MESSAGE = t`You must be a supporter, fan club member, or moderator to send this message.`
    const DM_RESTRICTION_MESSAGE_AGE_VERIFIED = t`You must be a supporter, fan club member, moderator, or have received a tip in the past week from this user to send this message.`

    const messagingView = useMessagingView()
    const { hoverRef, isHovered } = useHover()
    const { username = "" } = useParams<{ username: string }>()
    const { dmUnreadData, isMinimized, setIsMinimized, messagingInstanceId } =
        useMessagingContext()
    const { vvOffsetTop } = useVisualViewport()
    const { data: profile } = useGetProfileQuery(username)
    const [offsetData, setOffsetData] = useState<{
        username: string
        offset: string
    } | null>(null)
    const { trackNewConversationMessageSent, trackNewConversationExit } =
        useNewConversationTracking()
    const [
        triggerGetHistory,
        { data, isLoading, isFetching, error, isUninitialized },
    ] = useLazyGetHistoryQuery()
    const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false)

    const [markAsRead] = useMarkAsReadMutation()
    const dispatch = useAppDispatch()
    const loggedInUser = useAppContext().context.logged_in_user

    const messages = useMemo(() => {
        return data?.messages || []
    }, [data?.messages])

    const [showMenuMessageId, setShowMenuMessageId] = useState<string | null>(
        null,
    )
    const [isAllHistoryLoaded, setIsAllHistoryLoaded] = useState(false)
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false)
    const [visibleItems, setVisibleItems] = useState<VisibleItems>({
        startIndex: 0,
        stopIndex: 0,
    })
    const [showReport, setShowReport] = useState(false)
    const [showUserNote, setShowUserNote] = useState(false)
    const [pendingMinimize, setPendingMinimize] = useState(false)

    const listRef = useRef<VariableSizeList>(null)
    const listOuterRef = useRef<HTMLDivElement>(null)
    const messageHeights = useRef<Record<string, number>>({})
    const initialLoadDone = useRef(false)
    const oldMessagesLengthRef = useRef<number>(0)
    const isAdjustingScroll = useRef(false)
    const targetScrollIndex = useRef<number | null>(null)
    const scrollOffsetBeforeLoad = useRef<number>(0)
    const loadTimeoutRef = useRef<NodeJS.Timeout>(undefined)
    const restoreTimeoutRef = useRef<NodeJS.Timeout>(undefined)
    const currentPaginationRequestRef = useRef<{ abort: () => void } | null>(
        null,
    )
    const currentPaginationRequestUsernameRef = useRef<string | null>(null)
    const myUsername = useAppSelector(
        (state) => state.user.loggedInUser?.username,
    )
    const isNearBottom = useRef(true)
    const isListScrollable = useRef(false)
    const [showScrollDownButton, setShowScrollDownButton] = useState(false)
    // debounce to prevent flash on android keyboard open
    const debouncedShowScrollDownButton = useDebounce(
        showScrollDownButton,
        SCROLL_BUTTON_DEBOUNCE_DELAY,
    )
    const isUserTyping = useAppSelector((state) => state.userTyping[username])
    const [previousTypingState, setPreviousTypingState] = useState(false)
    const isScrolledUp = useRef(false)
    const [hasReceivedNewMessages, setHasReceivedNewMessages] = useState(false)
    const [messageInputHasFocus, setMessageInputHasFocus] = useState(false)
    const [reactionMessage, setReactionMessage] = useState<Message | undefined>(
        undefined,
    )
    const [showEmoticonDetails, setShowEmoticonDetails] = useState<
        IEmoticon | undefined
    >(undefined)
    const hasAddedRestrictionMessageRef = useRef(false)

    const lastQuery = useAppSelector(
        (state) =>
            messagingApi.endpoints.getThreads.select({
                offset: 0,
            })(state).originalArgs,
    )

    const { data: threadsData } = useGetThreadsQuery({
        offset: lastQuery?.offset ?? 0,
    })
    const itemCount = messages.length + 1 + (isUserTyping ? 1 : 0)

    const numUnread = useMemo(() => {
        return (
            threadsData?.threads.find(
                (thread) => thread.other_user.username === username,
            )?.num_unread ?? 0
        )
    }, [threadsData, username])

    const initialUnread = useAppSelector(
        (state) => state.messaging.initialUnread[username] ?? 0,
    )

    const getItemSize = useCallback(
        (index: number) => {
            if (index === 0) return isAllHistoryLoaded ? 0 : LOADER_HEIGHT
            if (index === messages.length + 1 && isUserTyping) {
                return messagingView.isDesktopConversation
                    ? TYPING_INDICATOR_HEIGHT_COMPACT
                    : TYPING_INDICATOR_HEIGHT
            }
            const messageId = messages[index - 1]?.i
            return messageHeights.current[messageId] || 0
        },
        [messages, isAllHistoryLoaded, isUserTyping],
    )

    const restoreScrollPosition = useCallback(
        (index: number) => {
            if (!listRef.current || !listOuterRef.current) return

            clearTimeout(restoreTimeoutRef.current)
            isAdjustingScroll.current = true
            const newContentHeight = Array.from({ length: index }, (_, i) =>
                getItemSize(i),
            ).reduce((sum, height) => sum + height, 0)

            const finalPosition =
                scrollOffsetBeforeLoad.current < SCROLL_THRESHOLD
                    ? newContentHeight
                    : newContentHeight + scrollOffsetBeforeLoad.current

            listRef.current.scrollTo(finalPosition - LOADER_HEIGHT)

            restoreTimeoutRef.current = setTimeout(() => {
                isAdjustingScroll.current = false
                targetScrollIndex.current = null
                restoreTimeoutRef.current = undefined
            }, 0)
        },
        [getItemSize],
    )

    const scrollToBottom = useCallback(() => {
        if (!listRef.current || !messages.length) return
        // if a typing indicator row is present, scroll to that.
        // otherwise, scroll to the last message.
        const targetIndex = messages.length + (isUserTyping ? 1 : 0)
        listRef.current?.scrollToItem(targetIndex, "end")

        if (listOuterRef.current) {
            listOuterRef.current.scrollTop = listOuterRef.current.scrollHeight
        }

        const SCROLL_DELAY_MS = messages[messages.length - 1]
            .is_temporary_tip_message
            ? 250
            : 30
        setTimeout(() => {
            requestAnimationFrame(() => {
                listRef?.current?.scrollToItem(targetIndex, "end")
            })
        }, SCROLL_DELAY_MS)
    }, [messages, isUserTyping])

    const handleHeightChange = useCallback(
        (index: number, height: number, messageId: string) => {
            if (height === 0 || messageHeights.current[messageId] === height)
                return

            messageHeights.current[messageId] = height

            if (listRef.current) {
                listRef.current.resetAfterIndex(index)

                if (
                    isAdjustingScroll.current &&
                    targetScrollIndex.current !== null
                ) {
                    restoreScrollPosition(targetScrollIndex.current)
                }
            }
        },
        [restoreScrollPosition],
    )

    const handleMarkAsRead = useCallback(() => {
        if (
            !isMinimized &&
            (numUnread > 0 || dmUnreadData.hasUnreadFromUser(username))
        ) {
            markAsRead(username).catch(() => {})
            dmUnreadData.removeUnreadRecipient(username)
        }
    }, [markAsRead, username, numUnread, isMinimized, dmUnreadData])

    const { newMessageLineIndex, isNewMessageLineVisible } = useNewMessageLine({
        messages,
        numUnread,
        isScrolledUp: isScrolledUp.current,
        isNearBottom: isNearBottom.current,
        initialUnread,
        hasReceivedNewMessages,
    })

    const handleScroll = useCallback(
        ({ scrollOffset }: { scrollOffset: number }) => {
            if (!isAdjustingScroll.current) {
                scrollOffsetBeforeLoad.current = scrollOffset
            }

            if (listOuterRef.current) {
                const listOuter = listOuterRef.current
                const scrolledPosition =
                    listOuter.scrollHeight - listOuter.scrollTop
                const listScrollable =
                    listOuter.clientHeight > 0 &&
                    listOuter.scrollHeight > listOuter.clientHeight
                const nearBottom =
                    !listScrollable ||
                    scrolledPosition <=
                        listOuter.clientHeight + NEAR_BOTTOM_THRESHOLD

                isNearBottom.current = nearBottom
                isScrolledUp.current = !nearBottom
                setShowScrollDownButton(!nearBottom)

                if (nearBottom) {
                    handleMarkAsRead()
                    isListScrollable.current = listScrollable
                }
            }

            if (
                !initialLoadDone.current ||
                !messages.length ||
                isFetching ||
                isAllHistoryLoaded ||
                isAdjustingScroll.current ||
                loadTimeoutRef.current ||
                isMinimized
            )
                return

            if (scrollOffset < SCROLL_THRESHOLD) {
                clearTimeout(loadTimeoutRef.current)
                loadTimeoutRef.current = setTimeout(() => {
                    setIsLoadingOlderMessages(true)
                    // Find first non temporary tip message for pagination offset
                    const firstNonTemporaryMessage = messages.find(
                        (msg) => !msg?.is_temporary_tip_message,
                    )
                    if (firstNonTemporaryMessage) {
                        setOffsetData({
                            username,
                            offset: firstNonTemporaryMessage.i,
                        })
                    }
                    loadTimeoutRef.current = undefined
                }, SCROLL_THROTTLE_DELAY)
            }
        },
        [
            messages,
            isFetching,
            isAllHistoryLoaded,
            handleMarkAsRead,
            username,
            isMinimized,
        ],
    )

    const resetScrollState = useCallback(() => {
        initialLoadDone.current = false
        oldMessagesLengthRef.current = 0
        setIsAllHistoryLoaded(false)
        setOffsetData(null)
        messageHeights.current = {}
        scrollOffsetBeforeLoad.current = 0
        targetScrollIndex.current = null
        isAdjustingScroll.current = false
        if (listRef.current) {
            listRef.current.scrollTo(0)
        }
    }, [])

    const addDmOpenConversationEvent = () => {
        const conversationIndex =
            threadsData?.threads?.findIndex(
                (thread) => thread.other_user.username === username,
            ) ?? 0

        const latestNonLogMessage = findNonLogMessage(
            messages,
            messages.length - 1,
            -1,
        )
        const timeSinceLastMessage = latestNonLogMessage
            ? Math.floor(Date.now() / 1000 - latestNonLogMessage.created_at)
            : 0

        addPageAction("DmOpenConversation", {
            source: messagingView.isMobile
                ? NEWRELIC_SOURCE.Mobile
                : NEWRELIC_SOURCE.Desktop,
            canTip: profile?.can_tip,
            hasUnread: initialUnread > 0,
            conversationIndex: conversationIndex,
            timeSinceLastMessage: timeSinceLastMessage,
        })
    }

    const emptyConversationStyles: CSSProperties = useMemo(() => {
        if (isChrome && isIOS) {
            return {
                paddingTop: vvOffsetTop,
                width: "100%",
            }
        }

        return {
            width: "100%",
            minHeight: vvOffsetTop,
            position: "absolute",
            bottom: vvOffsetTop ? 0 : undefined,
        }
    }, [vvOffsetTop])

    const virtualListStyles: CSSProperties | undefined = useMemo(() => {
        return vvOffsetTop
            ? {
                  position: "absolute",
                  bottom: 0,
              }
            : undefined
    }, [vvOffsetTop])

    useEffect(() => {
        setOffsetData(null)
        triggerGetHistory({ username, offset: undefined })
    }, [username, resetScrollState, triggerGetHistory])

    useEffect(() => {
        return () => {
            // Abort in flight pagination requests when the component unmounts since it can cause issues if the user navigates back to the page before the request completes.
            if (
                currentPaginationRequestRef.current &&
                currentPaginationRequestUsernameRef.current === username
            ) {
                currentPaginationRequestRef.current.abort()
                currentPaginationRequestRef.current = null
                currentPaginationRequestUsernameRef.current = null
            }
        }
    }, [username])

    useEffect(() => {
        if (offsetData && offsetData.username === username) {
            const paginationRequest = triggerGetHistory({
                username,
                offset: offsetData.offset,
            })
            currentPaginationRequestRef.current = paginationRequest
            currentPaginationRequestUsernameRef.current = username
        }
    }, [username, offsetData, triggerGetHistory])

    // Handle scroll to bottom after initial data loads.
    useLayoutEffect(() => {
        if (!messages.length || !listRef.current || isUninitialized) return

        if (!initialLoadDone.current) {
            oldMessagesLengthRef.current = messages.length
            addDmOpenConversationEvent()
            initialLoadDone.current = true
            setIsAllHistoryLoaded(messages.length < MESSAGES_BATCH_SIZE)
            scrollToBottom()
        }
    }, [messages, scrollToBottom, isUninitialized])

    // Handle scroll to bottom when sending or receiving a message.
    // When sending a message, always scroll to the bottom.
    // When receiving a message, scroll to bottom if user is near the bottom.
    useLayoutEffect(() => {
        if (!messages.length || !listRef.current || !listOuterRef.current)
            return

        const newLength = messages.length
        const oldLength = oldMessagesLengthRef.current
        const diff = newLength - oldLength

        if (isLoadingOlderMessages && diff > 0 && oldLength > 0) {
            isAdjustingScroll.current = true
            targetScrollIndex.current = diff + 1
            listRef?.current?.resetAfterIndex(0, true)
            restoreScrollPosition(diff + 1)
            setIsLoadingOlderMessages(false)
        }

        const latestMessage = messages[newLength - 1]
        const isFromMe = latestMessage?.from_user.username === myUsername
        const isSingleNewMessage = diff === 1

        const shouldScrollToBottom =
            isSendingMessage ||
            (isSingleNewMessage &&
                !isLoadingOlderMessages &&
                isNearBottom.current)

        if (isSingleNewMessage && !isFromMe) {
            setHasReceivedNewMessages(true)

            if (isNearBottom.current) {
                handleMarkAsRead()
            }
        }

        if (shouldScrollToBottom) {
            scrollToBottom()
        }
        if (isSendingMessage) {
            setIsSendingMessage(false)
        }
        oldMessagesLengthRef.current = newLength
    }, [
        messages,
        isSendingMessage,
        restoreScrollPosition,
        myUsername,
        isLoadingOlderMessages,
        handleMarkAsRead,
    ])

    useEffect(() => {
        if (!data) return
        setIsAllHistoryLoaded(!data.has_more)
    }, [data?.has_more])

    useEffect(() => {
        // scroll down to typing indicator if other user starts typing after MessageList has mounted
        if (
            messages.length > 0 &&
            listRef.current &&
            isUserTyping &&
            !previousTypingState
        ) {
            if (isNearBottom.current) {
                const typingIndicatorIndex = messages.length + 1
                listRef.current?.scrollToItem(typingIndicatorIndex, "end")
            }
        }
        setPreviousTypingState(isUserTyping)
    }, [
        isUserTyping,
        messages,
        listRef.current,
        isNearBottom.current,
        previousTypingState,
    ])

    const prevIsNewMessageLineVisibleRef = useRef<boolean>()
    useEffect(() => {
        const currentIsVisible = isNewMessageLineVisible
        const prevVisible = prevIsNewMessageLineVisibleRef.current

        if (
            currentIsVisible === true &&
            prevVisible === false &&
            isNearBottom.current
        ) {
            scrollToBottom()
        }

        prevIsNewMessageLineVisibleRef.current = currentIsVisible
    }, [isNewMessageLineVisible, scrollToBottom])

    /** Force react-window to recalculate heights when messages change since they're dynamically sized */
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0, true)
        }
    }, [messages])

    useEffect(() => {
        if (!isUninitialized) {
            const draft = draftCache.get(username)
            const hasNonLogMessage = !!messages.find((m) => !m.is_log_message)
            if (draft) {
                draftCache.set(username, {
                    ...draft,
                    hasExistingThread: hasNonLogMessage,
                })
            } else {
                draftCache.createGhostDraft(username, hasNonLogMessage)
            }
        }
    }, [messages, isUninitialized, username])

    // Add DM restriction message
    useEffect(() => {
        if (
            profile &&
            !profile.can_pm &&
            data !== undefined &&
            !isFetching &&
            !hasAddedRestrictionMessageRef.current
        ) {
            const restrictionMessage = loggedInUser?.is_age_verified
                ? DM_RESTRICTION_MESSAGE_AGE_VERIFIED
                : DM_RESTRICTION_MESSAGE

            dispatch(
                addMessageToHistory(
                    username,
                    createLogMessage(username, restrictionMessage),
                ),
            )

            hasAddedRestrictionMessageRef.current = true
        }
    }, [data, profile, username, loggedInUser, isFetching])

    useEffect(() => {
        dispatch(
            chatApi.util.prefetch("messageRenderOptions", undefined, {
                force: false,
            }),
        )
    }, [])

    const itemData = useMemo(
        () => ({
            messages,
            showMenuMessageId,
            setShowMenuMessageId,
            username,
            onHeightChange: handleHeightChange,
            isAllHistoryLoaded,
            isLoadingOlderMessages,
            isUserTyping,
            isNewMessageLineVisible,
            newMessageLineIndex,
            setReactionMessage,
            setShowEmoticonDetails,
        }),
        [
            messages,
            showMenuMessageId,
            username,
            handleHeightChange,
            isAllHistoryLoaded,
            isLoadingOlderMessages,
            isUserTyping,
            isNewMessageLineVisible,
            newMessageLineIndex,
            setReactionMessage,
            setShowEmoticonDetails,
        ],
    )

    const handleMinimize = () => {
        if (
            !isMinimized &&
            showUserNote &&
            messagingView.isDesktopConversation
        ) {
            setPendingMinimize(true)
            return
        }
        setIsMinimized((prev) => !prev)
    }

    const [showLoading, setShowLoading] = useState<boolean>(true)
    useEffect(() => {
        setShowLoading(
            (isFetching || isLoading) &&
                isNearBottom.current &&
                !initialLoadDone.current &&
                !messages.length,
        )
    }, [isFetching, isLoading, isNearBottom, initialLoadDone, messages])

    const showError =
        !isLoading &&
        error &&
        !(
            (error instanceof DOMException &&
                error.name === ABORT_ERROR_NAME) ||
            (error &&
                typeof error === "object" &&
                "name" in error &&
                error.name === ABORT_ERROR_NAME)
        )
    const showEmpty = !isUninitialized && !messages.length

    const getScrollButtonText = (): string => {
        const hasUnread = numUnread > 0
        const hasManyUnread = numUnread >= 10
        let text = t`Scroll to bottom`

        if (hasUnread) {
            text = hasManyUnread
                ? t`10+ new messages`
                : plural(numUnread, {
                      one: `${numUnread} new message`,
                      other: `${numUnread} new messages`,
                  })
        }
        return text
    }

    const onItemsRendered = ({
        visibleStopIndex,
        visibleStartIndex,
    }: {
        visibleStartIndex: number
        visibleStopIndex: number
    }) => {
        setVisibleItems({
            startIndex: visibleStartIndex,
            stopIndex: visibleStopIndex,
        })
    }

    // throttling to fix multiple viewport height changes on edge
    const lastExecutionTime = useRef(0)
    const onViewportHeightChange = (heightDelta?: number) => {
        const now = Date.now()
        if (
            now - lastExecutionTime.current <
            VIEWPORT_HEIGHT_CHANGE_THROTTLE_DELAY
        ) {
            return
        }
        lastExecutionTime.current = now

        requestAnimationFrame(() => {
            const list = listRef.current
            const outerContainer = listOuterRef.current

            // Case 1a: If Edge is near bottom just scroll to bottom
            if (isEdge && isNearBottom.current) {
                scrollToBottom()
                return
            }

            // Case 1b: No height change -> maintain position
            if (!heightDelta) {
                const shouldMaintainPosition =
                    list &&
                    (visibleItems.startIndex !== 0 || data?.has_more === false)

                if (shouldMaintainPosition) {
                    list.scrollToItem(visibleItems.stopIndex, "end")
                }
                return
            }

            // Case 2: Adjust scroll position when input height changes
            // - Case 2a: Near the bottom -> stay at bottom
            if (isNearBottom.current && messages.length > 0) {
                if (list) {
                    const lastItemIndex =
                        messages.length + (isUserTyping ? 1 : 0)
                    list.scrollToItem(lastItemIndex, "end")
                }
                if (outerContainer) {
                    setTimeout(() => {
                        outerContainer.scrollTop = outerContainer.scrollHeight
                    }, 10)
                }
                return
            }

            // - Case 2b: Normal height change (not near bottom)
            if (outerContainer) {
                setTimeout(() => {
                    const newScrollTop = outerContainer.scrollTop + heightDelta
                    outerContainer.scrollTo({
                        top: newScrollTop,
                        behavior: "auto",
                    })
                }, 10)
            }
        })
    }

    const otherUserMessages = useMemo(
        () =>
            messages?.filter(
                (msg: Message) => msg.from_user.username === username,
            ) ?? [],
        [messages, username],
    )

    const messageToReport = useMemo(
        () =>
            otherUserMessages.length === 0
                ? undefined
                : findNonLogMessage(
                      otherUserMessages,
                      otherUserMessages.length - 1,
                      -1,
                  ),
        [otherUserMessages],
    )

    const isReportEnabled = !!messageToReport

    const handleReportClick = useCallback(() => {
        setShowReport(true)
    }, [])

    const handleNoteClick = useCallback(() => {
        setShowUserNote(true)
        if (
            messagingView.isDesktopConversation &&
            listOuterRef.current &&
            listRef.current
        ) {
            listRef.current.scrollTo(
                listOuterRef.current.scrollTop +
                    USER_NOTE_DESKTOP_SCROLL_OFFSET,
            )
        }
    }, [])

    const thread = threadsData?.threads.find(
        (t) => t.other_user.username === username,
    )

    const otherUser = thread?.other_user

    const [adjustForNewConversation, setAdjustForNewConversation] =
        useState(false)

    useEffect(() => {
        if (vvOffsetTop === 0) {
            setAdjustForNewConversation(false)
            return
        }
        // buffer of 10 messages
        // we don't need to continue calculating because we know height of list is sufficient to not require adjusting
        if (messages.length <= 10) {
            const messageHeight = Object.values(messageHeights.current).reduce(
                (sum, h) => sum + h,
                0,
            )

            setAdjustForNewConversation(messageHeight < vvOffsetTop)
        }
        // intentionally not including messages and messageHeights in dependency array
        // effect should only re-run when vvOffsetTop changes
    }, [vvOffsetTop])

    const getVirtualListHeight = useCallback(
        (height: number) => (adjustForNewConversation ? vvOffsetTop : height),
        [adjustForNewConversation, vvOffsetTop],
    )

    const handleNoteClose = () => {
        if (pendingMinimize && messagingView.isDesktopConversation) {
            setPendingMinimize(false)
            setShowUserNote(false)
            setIsMinimized((prev) => !prev)
        }
        setShowUserNote(false)
    }

    const handleNoteCancelClose = () => {
        if (messagingView.isDesktopConversation) {
            setPendingMinimize(false)
        }
    }

    return (
        <div
            className={classNames("messages-list-container", {
                desktop: messagingView.isDesktopConversation,
                ["desktop-unified"]: messagingView.isDesktopUnified,
            })}
            ref={hoverRef}
        >
            <div
                className={classNames("messages-list", {
                    mobile: messagingView.isMobile,
                    ["desktop-unified"]: messagingView.isDesktopUnified,
                    desktop: messagingView.isDesktopConversation,
                    minimized: isMinimized,
                    android: isAndroid,
                })}
            >
                <MessageHeader
                    username={username}
                    onMinimize={handleMinimize}
                    numUnread={numUnread}
                    onReportClick={handleReportClick}
                    onNoteClick={handleNoteClick}
                    isReportEnabled={isReportEnabled}
                    messageInputHasFocus={messageInputHasFocus}
                    trackNewConversationExit={trackNewConversationExit}
                    isHovered={isHovered}
                />
                <div
                    className={classNames("messages-content", {
                        hidden: isMinimized,
                        desktop: messagingView.isDesktopConversation,
                        "desktop-unified": messagingView.isDesktopUnified,
                    })}
                >
                    <ToastManager
                        id={`${messagingInstanceId ?? "message-list"}`}
                    />
                    {debouncedShowScrollDownButton && !showUserNote && (
                        <ScrollButton
                            className="scroll-button"
                            onClick={() => {
                                scrollToBottom()
                                addPageAction("DmScrollToBottom", {
                                    hasNewMessages: numUnread > 0,
                                })
                            }}
                            text={getScrollButtonText()}
                        />
                    )}
                    {showLoading && (
                        <div className="messages-status loading">
                            <Spinner size="md" />
                        </div>
                    )}
                    {showError ? (
                        <div className="messages-status">
                            Error loading messages
                        </div>
                    ) : showEmpty ? (
                        <div style={emptyConversationStyles}>
                            <MessageRowDisclaimer username={username} />
                        </div>
                    ) : (
                        <>
                            <HeightPreCalculator
                                messages={messages}
                                messageHeights={messageHeights}
                                itemData={itemData}
                            />
                            <AutoSizer doNotBailOutOnEmptyChildren>
                                {({ height, width }) => (
                                    <VirtualList<MessageRowData>
                                        className={classNames("virtual-list")}
                                        style={virtualListStyles}
                                        height={getVirtualListHeight(height)}
                                        width={width}
                                        itemCount={itemCount}
                                        itemSize={getItemSize}
                                        overscanCount={OVERSCAN_COUNT}
                                        onScroll={handleScroll}
                                        itemData={itemData}
                                        ref={listRef}
                                        outerRef={listOuterRef}
                                        onItemsRendered={onItemsRendered}
                                        useIsScrolling
                                    >
                                        {Row}
                                    </VirtualList>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
                {!isMinimized && TEST_MEDIA_MESSAGE && (
                    <>
                        <MessageMedia mockSufficientTokens />
                        <MessageMedia mockSufficientTokens={false} />
                    </>
                )}
                {!isMinimized && (
                    <MessageInputBar
                        scrollToBottom={scrollToBottom}
                        onViewportHeightChange={onViewportHeightChange}
                        onMessageInputFocusChange={(hasFocus) =>
                            setMessageInputHasFocus(hasFocus)
                        }
                        trackNewConversationMessageSent={
                            trackNewConversationMessageSent
                        }
                        isDataLoading={showLoading}
                        shouldHide={showUserNote}
                        onSendMessage={() => {
                            setIsSendingMessage(true)
                        }}
                    />
                )}
                {showReport && (
                    <ReportMessage
                        onClose={() => setShowReport(false)}
                        message={messageToReport ?? undefined}
                    />
                )}
                {showUserNote && (
                    <UserNote
                        onClose={handleNoteClose}
                        onCancelClose={handleNoteCancelClose}
                        shouldCloseWithWarning={pendingMinimize}
                    />
                )}
                {reactionMessage?.reactions !== undefined &&
                    Object.entries(reactionMessage.reactions).length > 0 && (
                        <Reactions
                            onClose={() => setReactionMessage(undefined)}
                            reactionMessage={reactionMessage}
                            otherUser={otherUser}
                            thisUsername={myUsername}
                        />
                    )}
                {showEmoticonDetails && (
                    <EmoticonDetails
                        emoticon={showEmoticonDetails}
                        onClose={() => setShowEmoticonDetails(undefined)}
                    />
                )}
            </div>
        </div>
    )
}
